import { SharedEditingConfig } from "../agent-config-options";

/**
 * Default master switch for the shared-editing convention. When no
 * config is supplied, the convention ships **enabled** so every agent
 * that edits an index file follows the single-entry / verify /
 * re-sort protocol.
 *
 * @see SharedEditingConfig
 */
export const DEFAULT_SHARED_EDITING_ENABLED = true;

/**
 * Default list of path patterns considered "shared index files". The
 * patterns are plain glob strings rendered verbatim into the rule body
 * — agents match against them when deciding whether the shared-editing
 * contract applies to the file they are about to edit.
 *
 * The defaults cover the registry / index files every configulator
 * consumer ships by convention:
 *
 * - A monorepo-wide docs site at `/docs` with one or more `index.md` /
 *   `README.md` registry tables.
 * - Category landing pages under `docs/src/content/docs/**` that list
 *   every profile, requirement, or capability in their category.
 * - Feature matrices produced by the `software-profile` bundle.
 *
 * Consumers can replace the list outright via `sharedIndexPaths` or
 * append project-specific registries.
 *
 * @see SharedEditingConfig
 */
export const DEFAULT_SHARED_INDEX_PATHS: ReadonlyArray<string> = [
  "docs/src/content/docs/**/index.md",
  "docs/src/content/docs/**/README.md",
  "docs/src/content/docs/**/feature-matrix.md",
];

/**
 * Default conflict-resolution strategy rendered into the rule body.
 * `rebase` matches the `git pull --rebase` workflow every
 * configulator-managed repo already uses for feature branches; the
 * alternative (`merge`) is documented for projects that keep a
 * merge-commit-only history.
 *
 * @see SharedEditingConfig
 */
export const DEFAULT_SHARED_EDITING_CONFLICT_STRATEGY: "rebase" | "merge" =
  "rebase";

/**
 * Default for whether the convention renders the commit-path
 * verification protocol (read-back + single-row assertion). The
 * verification step is cheap, catches staging / path bugs that would
 * otherwise land on the branch, and is the core safety net the openhi
 * reference promotes — so it ships **on** by default.
 *
 * @see SharedEditingConfig
 */
export const DEFAULT_SHARED_EDITING_VERIFY_COMMIT = true;

/**
 * Default for whether the convention emits the
 * `.claude/procedures/verify-index-row.sh` helper to disk. The helper
 * is opt-in because many consumers prefer to do the verification
 * inline via the documented `git show HEAD:<path>` recipe rather than
 * shell out to a dedicated script. Consumers that want the script
 * available to sub-agents enable the emission explicitly.
 *
 * @see SharedEditingConfig
 */
export const DEFAULT_SHARED_EDITING_EMIT_HELPER = false;

/**
 * Allowed values for `SharedEditingConfig.conflictStrategy`. Exported
 * so consumers can reference the canonical set without hard-coding
 * literals.
 */
export const SHARED_EDITING_CONFLICT_STRATEGY_VALUES = [
  "rebase",
  "merge",
] as const;

/**
 * Fully-resolved shared-editing settings. Every field is defaulted so
 * downstream renderers can reason about a single canonical shape.
 */
export interface ResolvedSharedEditing {
  readonly enabled: boolean;
  readonly sharedIndexPaths: ReadonlyArray<string>;
  readonly verifyCommit: boolean;
  readonly conflictStrategy: "rebase" | "merge";
  readonly emitHelper: boolean;
}

/**
 * Resolve a (possibly absent) `SharedEditingConfig` into a canonical
 * `ResolvedSharedEditing` with every field filled in. Unset fields
 * cascade from their documented defaults.
 *
 * Malformed configs — empty / whitespace-only `sharedIndexPaths`
 * entry, unknown `conflictStrategy` — throw a descriptive `Error`.
 */
export function resolveSharedEditing(
  config?: SharedEditingConfig,
): ResolvedSharedEditing {
  const sharedIndexPaths =
    config?.sharedIndexPaths ?? DEFAULT_SHARED_INDEX_PATHS;
  assertValidSharedIndexPaths(sharedIndexPaths);

  const conflictStrategy =
    config?.conflictStrategy ?? DEFAULT_SHARED_EDITING_CONFLICT_STRATEGY;
  assertValidConflictStrategy(conflictStrategy);

  return {
    enabled: config?.enabled ?? DEFAULT_SHARED_EDITING_ENABLED,
    sharedIndexPaths,
    verifyCommit: config?.verifyCommit ?? DEFAULT_SHARED_EDITING_VERIFY_COMMIT,
    conflictStrategy,
    emitHelper: config?.emitHelper ?? DEFAULT_SHARED_EDITING_EMIT_HELPER,
  };
}

/**
 * Synth-time validation hook. Throws a descriptive `Error` when the
 * supplied `SharedEditingConfig` is malformed. Called by
 * `AgentConfig.preSynthesize` before any rendering so a misconfigured
 * convention fails the build instead of silently shipping broken
 * shared-editing guidance. Returns the resolved config unchanged so
 * callers can write `const se = validateSharedEditingConfig(config)`
 * in one line.
 *
 * Malformed cases rejected here:
 *
 * - `sharedIndexPaths` contains an empty / whitespace-only entry, or
 *   the array is supplied but empty.
 * - `conflictStrategy` is not one of `"rebase"` / `"merge"`.
 */
export function validateSharedEditingConfig(
  config?: SharedEditingConfig,
): ResolvedSharedEditing {
  return resolveSharedEditing(config);
}

/**
 * Render the full body for the `shared-editing-safety` rule shipped
 * by the `base` bundle. The rule documents:
 *
 * - The catalog of shared index files the contract applies to.
 * - The pre-edit read-latest protocol (pull + re-read before editing).
 * - The single-entry, deterministic-sort row-insert rule.
 * - The commit-path verification step (read-back + count assertion).
 * - The merge-conflict resolution recipe (rebase, re-sort, re-verify).
 *
 * When the convention is disabled, the rule renders a short stub that
 * tells agents the project does not enforce the convention and that
 * concurrent edits to shared index files may require manual conflict
 * resolution.
 */
export function renderSharedEditingRuleContent(
  se: ResolvedSharedEditing,
): string {
  if (!se.enabled) {
    return [
      "# Shared-Editing Safety",
      "",
      "**Shared-editing safety is disabled in this project.** Agents",
      "may edit index files without running the single-entry /",
      "verify / re-sort protocol documented by configulator. When two",
      "agents complete work in parallel and both write to the same",
      "registry, humans must resolve any resulting merge conflicts",
      "manually.",
      "",
      "Enable the convention via",
      "`AgentConfigOptions.sharedEditing.enabled = true`.",
    ].join("\n");
  }

  const lines: Array<string> = [
    "# Shared-Editing Safety",
    "",
    "Multiple concurrent agent sessions frequently need to edit the",
    "same **shared index file** — registry `README.md` / `index.md`",
    "row tables, category landing pages, feature matrices. Without a",
    "shared contract, parallel sessions conflict on the index even",
    "when their content contributions are completely independent, and",
    "the resolver may accidentally drop a row when reconciling the",
    "conflict.",
    "",
    "This rule codifies the convention every agent follows when",
    "touching a shared index file: pull the latest, make a",
    "single-entry edit in deterministic sort order, verify the row",
    "landed in the commit, and follow a scripted recipe when a",
    "conflict does arise.",
    "",
    "## What Counts as a Shared Index File",
    "",
    "Agents treat any file matching one of the following patterns as",
    "a shared index — the contract in this rule applies before",
    "committing to it:",
    "",
    ...se.sharedIndexPaths.map((p) => `- \`${p}\``),
    "",
    "Registry tables, category landing pages, and feature matrices",
    "embedded inside a larger document are also shared indexes — the",
    "table itself is the index, not the file. Treat those sections",
    "as append-only where possible: add a new row instead of",
    "rewriting existing rows.",
    "",
    "## Index File Format Contract",
    "",
    "Every shared index follows the same three-part format so the",
    "insert position is mechanical rather than editorial:",
    "",
    "1. **Header** — a stable opening (title, prose intro, table",
    "   header row). Agents never edit the header when adding a row.",
    "2. **Separator** — a markdown table separator (`|---|---|`) or",
    "   an equivalent horizontal rule between header and rows.",
    "3. **Rows** — one entry per shared item, sorted by a single",
    "   deterministic key documented at the top of the file",
    "   (alphabetical on the first column, chronological on a date",
    "   column, etc.). If the file does not document a key, sort",
    "   alphabetically on the first column.",
    "",
    "## Pre-Edit Protocol",
    "",
    "Before writing a new row to a shared index file:",
    "",
    "1. **Pull the latest default branch into your working branch**:",
    "",
    "   ```bash",
    "   git fetch origin",
    `   git pull --${se.conflictStrategy} origin <default-branch>`,
    "   ```",
    "",
    "2. **Read the current file** from the updated checkout — not a",
    "   cached copy from earlier in the session. Identify the header,",
    "   separator, and existing rows.",
    "",
    "3. **Compute the insert position** by walking the existing rows",
    "   in declared sort order and finding the first row whose sort",
    "   key is greater than your new row's key.",
    "",
    "4. **Insert exactly one row** for your new entry. Do not rewrite",
    "   neighbouring rows, do not reorder the file, and do not batch",
    "   unrelated index changes into the same edit.",
    "",
    "5. **Commit the index edit immediately**, in its own focused",
    "   commit. Small index-only commits keep the merge-conflict",
    "   surface tiny; bundling index rows with large content changes",
    "   makes conflict resolution harder.",
    "",
  ];

  if (se.verifyCommit) {
    lines.push(
      "## Commit-Path Verification",
      "",
      "After committing the index edit, confirm the row landed in the",
      "commit before proceeding. This catches the class of failures",
      "where the file was written to the working tree but not staged",
      "— the test suite passes, the index looks right in the editor,",
      "and the row silently fails to ship.",
      "",
      "Run the following check before pushing:",
      "",
      "```bash",
      "# 1. Confirm the index file is in the last commit.",
      "git show --name-only --pretty=format: HEAD | grep -Fxq '<index-path>' \\",
      "  || { echo 'ERROR: index file not in last commit'; exit 1; }",
      "",
      "# 2. Confirm your row appears exactly once in the committed file.",
      "count=$(git show HEAD:'<index-path>' | grep -Fc '<row-unique-marker>')",
      '[ "$count" = "1" ] \\',
      '  || { echo "ERROR: row count $count, expected 1"; exit 1; }',
      "```",
      "",
      "`<row-unique-marker>` is a substring of the new row that is",
      "unique across the whole file — typically the sort key, or the",
      "link target the new row wraps. The `=1` assertion rejects both",
      "the missing-row case (count `0`) and the duplicate-row case",
      "(count `>=2`) that happens after a mis-merged conflict.",
      "",
    );

    if (se.emitHelper) {
      lines.push(
        "The bundled `.claude/procedures/verify-index-row.sh` helper",
        "runs the same two-step check in a single invocation — pass it",
        "the index path and a unique-marker substring:",
        "",
        "```bash",
        ".claude/procedures/verify-index-row.sh <index-path> <row-unique-marker>",
        "```",
        "",
        "The helper exits non-zero with a descriptive diagnostic on",
        "any failure, so it plugs directly into pre-push hooks and CI",
        "checks without further glue.",
        "",
      );
    }
  }

  lines.push(
    "## Defer Shared-Index Commit to Final Pre-Push Step",
    "",
    "Even with the single-entry, deterministic-sort discipline above,",
    "two sessions that **prepare** their row inserts at the same time",
    "still race on push: whichever session pushes second sees the",
    "first session's commit on the remote and has to rebase its own",
    "commit on top, regenerating the same insert position calculation",
    "against a now-changed file. Repeated rebases multiply the chance",
    "of a mis-merge that silently drops a row.",
    "",
    "Sessions that produce both content and a shared-index row insert",
    "shrink this race window by deferring the index commit to the",
    "**final pre-push step** — after every content commit has landed",
    "locally, and immediately before `git push`:",
    "",
    "1. **Commit content first.** Write and commit every non-index",
    "   change that the session produces (profile body, transcript",
    "   extraction, requirement document, etc.) in its own focused",
    "   commit or commits.",
    "2. **Rebase against the remote default branch** before touching",
    "   the shared index:",
    "",
    "   ```bash",
    "   git fetch origin",
    `   git pull --${se.conflictStrategy} origin <default-branch>`,
    "   ```",
    "",
    "3. **Re-read the shared index** from the now-up-to-date working",
    "   tree. Another session may have appended a row while this",
    "   session's content commits were in flight.",
    "4. **Re-compute the insert position** in declared sort order",
    "   against the freshly-read rows. Do not assume the position",
    "   computed earlier in the session is still correct.",
    "5. **Insert the row and commit the index edit on its own** —",
    "   one focused commit whose only file is the shared index. Run",
    "   the commit-path verification (above) against that commit",
    "   before continuing.",
    "6. **Push immediately.** The shorter the wall-clock gap between",
    "   the rebase / re-read in step 2 and the push, the smaller the",
    "   window in which another session can land a competing row.",
    "",
    "Per-agent workflows that touch a shared index file should call",
    "out this defer-to-final-commit sequence explicitly — see the",
    "`meeting-analyst` and `software-profile-analyst` sub-agent",
    "prompts for the canonical wording. The pattern is mechanical",
    "(rebase, re-read, re-compute, focused commit, push) rather than",
    "editorial, so the same recipe applies to every shared-index",
    "row-producing agent regardless of what content surrounds it.",
    "",
    "## Merge-Conflict Resolution",
    "",
    "When `git push` reports a conflict on a shared index file (two",
    "agents both added a row, both pushed), resolve it with the",
    "following recipe instead of hand-merging the conflict markers:",
    "",
    "```bash",
    `git pull --${se.conflictStrategy} origin <branch>`,
    "# If the conflict is in a shared index file:",
    "# 1. Abort any auto-merge that dropped either row.",
    "# 2. Re-read the updated file (now contains the other agent's row).",
    "# 3. Re-compute the insert position for your row in sort order.",
    "# 4. Re-insert your row (single entry, correct position).",
    "# 5. Stage the resolved file and continue.",
    "git add <index-path>",
    `git ${se.conflictStrategy === "rebase" ? "rebase --continue" : "commit"}`,
    "git push",
    "```",
    "",
    "After resolution, re-run the commit-path verification step to",
    "confirm both rows (yours and the other agent's) appear in the",
    "final committed file. A count of `2` against the column-header",
    "separator, or a count of `1` against each agent's unique marker,",
    "proves both contributions survived the merge.",
    "",
    "## Append-Only Where Possible",
    "",
    "Prefer append-only edits on shared indexes. Adding a row rarely",
    "conflicts; rewriting or reordering existing rows almost always",
    "does. If a shared index needs a structural change (column",
    "added, sort key changed), file a dedicated issue for the change",
    "rather than bundling it into a content-contributing PR.",
    "",
    "## Section Index Page Shape",
    "",
    "When any agent creates or updates an `index.md` (or `README.md`)",
    "in a docs subdirectory under a Starlight content root, the file's",
    "body must include:",
    "",
    "1. **A 1–2 paragraph summary** of the section's purpose and how",
    "   it fits into the larger research, requirements, or capability",
    "   area. Readers landing on the page should understand what's in",
    "   the section without falling back to the sidebar.",
    "",
    "2. **A grouped, linked listing of the directory's children**",
    "   (table or bullet list). When a natural taxonomy exists (e.g.",
    "   organizations grouped by sub-segment, regulations grouped by",
    "   jurisdiction, capabilities grouped by tier), use it. Otherwise",
    "   sort alphabetically by title. Every listing entry must link",
    "   to the child page.",
    "",
    "3. **No body `# Heading`.** Starlight renders the frontmatter",
    "   `title:` as the page H1 automatically — a body H1 produces a",
    "   duplicate. The same no-body-H1 contract that applies to skill",
    "   templates and inline agent templates also applies to every",
    "   index page emitted by an agent.",
    "",
    "### When this applies",
    "",
    "The convention applies to every `index.md` / `README.md` file",
    "covered by the shared-index path globs listed above:",
    "",
    "- `docs/src/content/docs/**/index.md`",
    "- `docs/src/content/docs/**/README.md`",
    "",
    "Any agent that scaffolds a new directory under a Starlight",
    "content root MUST populate the directory's index page following",
    "this contract — a one-sentence stub is not acceptable.",
    "",
    "### Reference shapes",
    "",
    "Existing rich indexes are the canonical reference shape. Examples:",
    "",
    "- A `<MEETINGS_ROOT>/<YYYY-MM-DD>-<slug>/index.md` populated by",
    "  the `agenda-analyst` bundle: frontmatter `title` /",
    "  `description`, a 2–4 sentence summary, and a `## Documents`",
    "  section listing every page in the folder.",
    "- A regulations scope index that opens with two paragraphs of",
    "  context and groups every linked regulation under a",
    "  `## Regulations` table by jurisdiction.",
    "- A standards-organizations index that groups every linked",
    "  organization under a `## Organizations` heading by role",
    "  (governance, working group, implementer).",
    "",
    "### Out of scope",
    "",
    "- Auto-generated child listings via Astro/Starlight components.",
    "  Agents emit hand-curated tables or bullet lists; the rule",
    "  defines the shape, not the rendering technology.",
    "- Backfilling pre-existing stub indexes is a downstream-consumer",
    "  cleanup task, not a configulator change.",
  );

  return lines.join("\n");
}

/**
 * Render the short shared-editing hook section injected into a
 * phased-agent bundle's workflow rule (company-profile,
 * people-profile, software-profile, etc.). The section cites the
 * full contract documented in the base bundle's
 * `shared-editing-safety` rule so individual bundles stay DRY.
 *
 * When the convention is disabled, the function returns an empty
 * string so callers can no-op their append path.
 */
export function renderSharedEditingBundleHook(
  se: ResolvedSharedEditing,
  bundleLabel: string,
): string {
  if (!se.enabled) {
    return "";
  }

  return [
    "## Shared Index Editing",
    "",
    `Every ${bundleLabel} run that adds a row to a shared registry`,
    "or feature-matrix file follows the shared-editing contract: pull",
    "the latest default branch before editing, insert exactly one row",
    "in deterministic sort position, commit the index edit in its own",
    "focused commit, and verify the row is present in the commit",
    "before pushing. **Defer the index commit to the final pre-push",
    "step** — land every content commit first, then rebase against",
    "the remote default branch, re-read the index, re-compute the",
    "insert position, write the row, commit the index on its own,",
    "and push immediately. See the `shared-editing-safety` rule for",
    "the full protocol, the list of files covered, the",
    "defer-to-final-commit sequence, and the merge-conflict",
    "resolution recipe.",
  ].join("\n");
}

/**
 * Render the `.claude/procedures/verify-index-row.sh` helper script.
 * Exported so `AgentConfig` can register it as an `AgentProcedure`
 * when the consumer opts in via `emitHelper: true`.
 *
 * The script takes two positional arguments:
 *
 * 1. `<index-path>` — repo-relative path to the shared index file.
 * 2. `<row-unique-marker>` — substring unique to the new row.
 *
 * It exits non-zero on any of the following:
 *
 * - Wrong argument count.
 * - Index file is not present in `HEAD` (i.e. not staged).
 * - The unique-marker substring appears zero times (row missing)
 *   or more than once (duplicate row from a mis-merged conflict).
 */
export function renderSharedEditingHelperScript(
  _se: ResolvedSharedEditing,
): string {
  return [
    "#!/usr/bin/env bash",
    "# verify-index-row.sh — Verify a shared-index row landed in HEAD.",
    "#",
    "# Usage:",
    "#   .claude/procedures/verify-index-row.sh <index-path> <row-unique-marker>",
    "#",
    "# Exits 0 when <index-path> is in the last commit and",
    "# <row-unique-marker> appears exactly once in the committed file.",
    "# Exits non-zero with a clear diagnostic on stderr on any failure.",
    "#",
    "# This helper implements the commit-path verification step",
    "# documented by the shared-editing-safety rule.",
    "",
    "set -uo pipefail",
    "",
    "err() {",
    '  printf "verify-index-row.sh: %s\\n" "$*" >&2',
    "}",
    "",
    "if [[ $# -ne 2 ]]; then",
    '  err "expected 2 arguments, got $#"',
    '  err "usage: verify-index-row.sh <index-path> <row-unique-marker>"',
    "  exit 2",
    "fi",
    "",
    'index_path="$1"',
    'marker="$2"',
    "",
    'if [[ -z "${index_path// /}" ]]; then',
    '  err "index path must be non-empty"',
    "  exit 2",
    "fi",
    "",
    'if [[ -z "${marker// /}" ]]; then',
    '  err "row unique marker must be non-empty"',
    "  exit 2",
    "fi",
    "",
    "# 1. Confirm the index file is in the last commit.",
    'if ! git show --name-only --pretty=format: HEAD 2>/dev/null | grep -Fxq "$index_path"; then',
    "  err \"index file '$index_path' not in last commit (staging bug?)\"",
    "  exit 3",
    "fi",
    "",
    "# 2. Confirm the marker appears exactly once in the committed file.",
    'committed=$(git show "HEAD:$index_path" 2>/dev/null) || {',
    "  err \"could not read '$index_path' from HEAD\"",
    "  exit 4",
    "}",
    "",
    'count=$(printf "%s" "$committed" | grep -Fc "$marker" || true)',
    "",
    'if [[ "$count" -eq 0 ]]; then',
    "  err \"row marker '$marker' not present in '$index_path' (row missing)\"",
    "  exit 5",
    'elif [[ "$count" -gt 1 ]]; then',
    "  err \"row marker '$marker' appears $count times in '$index_path' (duplicate from mis-merged conflict?)\"",
    "  exit 6",
    "fi",
    "",
    "exit 0",
  ].join("\n");
}

/*******************************************************************************
 *
 * Internal helpers
 *
 ******************************************************************************/

function assertValidSharedIndexPaths(value: ReadonlyArray<string>): void {
  if (!Array.isArray(value)) {
    throw new Error(
      `SharedEditingConfig.sharedIndexPaths must be an array; got ${JSON.stringify(
        value,
      )}`,
    );
  }
  if (value.length === 0) {
    throw new Error(
      "SharedEditingConfig.sharedIndexPaths must not be empty — supply at least one path pattern or omit the field to use the defaults",
    );
  }
  for (const entry of value) {
    if (typeof entry !== "string" || entry.trim().length === 0) {
      throw new Error(
        `SharedEditingConfig.sharedIndexPaths entries must be non-empty strings; got ${JSON.stringify(
          entry,
        )}`,
      );
    }
  }
}

function assertValidConflictStrategy(value: string): void {
  if (
    !SHARED_EDITING_CONFLICT_STRATEGY_VALUES.includes(
      value as "rebase" | "merge",
    )
  ) {
    throw new Error(
      `SharedEditingConfig.conflictStrategy must be one of ${SHARED_EDITING_CONFLICT_STRATEGY_VALUES.join(
        ", ",
      )}; got ${JSON.stringify(value)}`,
    );
  }
}
